// Module 11 contracts: logical credentials, external operations and the recovery queue, access
// connectors and zone mapping, access events and badge templates (ADR-018, credentials stream).
// Messages are i18n keys (PD-015).
import { z } from 'zod';

const Uuid = z.uuid();
const trimmed = (max: number) => z.string().trim().min(1, 'v.required').max(max, 'v.tooLong');
const Version = z.number().int().min(1);
const Note = trimmed(1000);

export const TOKEN_TYPE_VALUES = ['card', 'pin', 'qr', 'mobile'] as const;
export const REVOKE_REASON_VALUES = ['departure', 'card_lost', 'cancellation', 'expiry', 'removal', 'manual'] as const;
export const BADGE_FIELD_VALUES = ['visitorName', 'organisation', 'hostName', 'siteName', 'destination', 'validUntil', 'reference', 'category', 'photo', 'referenceQr'] as const;
export const BADGE_ACCESS_STATUS_VALUES = ['any', 'with_credential', 'without_credential'] as const;
export const SIM_BEHAVIOUR_VALUES = ['succeed', 'reject', 'timeout', 'late', 'silent', 'broaden'] as const;

// ------------------------------------------------------------------ connectors (PB-11-07, PB-11-10)
export const CapabilitiesInput = z
  .object({
    tokenTypes: z.array(z.enum(TOKEN_TYPE_VALUES)).min(1).max(4),
    schedules: z.array(z.enum(['continuous', 'daily'])).min(1).max(2),
    granularityMinutes: z.number().int().min(1).max(1440),
    maxValidityDays: z.number().int().min(1).max(3660),
    readBack: z.boolean(),
    events: z.boolean(),
    maxZones: z.number().int().min(1).max(1000),
  })
  .partial();

export const CredentialRulesInput = z
  .object({
    allowedTokenTypes: z.array(z.enum(TOKEN_TYPE_VALUES)).max(4),
    includeEnclosingZones: z.boolean(),
    commonZoneIds: z.array(Uuid).max(50),
    activationLeadMinutes: z.number().int().min(0).max(1440),
    allowNarrowing: z.boolean(),
    expiryAction: z.enum(['revoke', 'trust_provider_schedule']),
    lateEventMinutes: z.number().int().min(0).max(1440),
    operationTimeoutMs: z.number().int().min(200).max(120_000),
    autoReadBackSeconds: z.number().int().min(0).max(86_400),
    maxAutoRevocations: z.number().int().min(1).max(20),
  })
  .partial();

/** Non-secret adapter settings. The simulator's behaviour lets every provider outcome be exercised. */
export const ConnectorSettingsInput = z
  .object({
    behaviour: z.object({ issue: z.enum(SIM_BEHAVIOUR_VALUES), revoke: z.enum(SIM_BEHAVIOUR_VALUES) }).partial(),
    readBack: z.enum(['normal', 'unavailable']),
    // ADR-025 (adapters stream): endpoint of the generic REST/JSON adapter (passage-access-rest/1).
    baseUrl: z.string().trim().url('v.invalid').max(300),
    paths: z.object({ issue: z.string(), revoke: z.string(), readBack: z.string(), events: z.string(), health: z.string() }).partial(),
  })
  .partial();

export const CreateConnectorRequest = z.object({
  name: trimmed(120),
  adapter: z.string().trim().regex(/^[a-z][a-z0-9-]{1,39}$/, 'v.invalid'),
  capabilities: CapabilitiesInput.optional(),
  settings: ConnectorSettingsInput.optional(),
  rules: CredentialRulesInput.optional(),
  secretRef: z.string().trim().regex(/^[A-Za-z0-9_./-]{1,120}$/, 'v.invalid').nullable().optional(),
});
export const UpdateConnectorRequest = z.object({
  expectedVersion: Version,
  name: trimmed(120).optional(),
  status: z.enum(['active', 'disabled']).optional(),
  capabilities: CapabilitiesInput.optional(),
  settings: ConnectorSettingsInput.optional(),
  rules: CredentialRulesInput.optional(),
  secretRef: z.string().trim().regex(/^[A-Za-z0-9_./-]{1,120}$/, 'v.invalid').nullable().optional(),
});
export const CreateMappingRequest = z.object({
  locationId: Uuid,
  externalRef: z.string().trim().regex(/^[A-Za-z0-9][A-Za-z0-9 ._:/-]{0,119}$/, 'cred.v.externalRef'),
});
export const SimulateEventRequest = z.object({
  credentialId: Uuid.optional(),
  externalRef: z.string().trim().min(1).max(200).optional(),
  doorRef: z.string().trim().min(1).max(120),
  direction: z.enum(['entry', 'exit', 'unknown']).default('entry'),
  result: z.enum(['granted', 'denied']).default('granted'),
  occurredAt: z.iso.datetime({ offset: true }).optional(),
});

export interface CapabilitiesDto {
  readonly tokenTypes: string[];
  readonly schedules: string[];
  readonly granularityMinutes: number;
  readonly maxValidityDays: number;
  readonly readBack: boolean;
  readonly events: boolean;
  readonly maxZones: number;
}
export interface ConnectorDto {
  readonly id: string;
  readonly siteId: string;
  readonly name: string;
  readonly adapter: string;
  readonly status: 'active' | 'disabled';
  readonly capabilities: CapabilitiesDto;
  readonly settings: Record<string, unknown>;
  readonly rules: Record<string, unknown>;
  readonly secretRef: string | null;
  readonly validatedAt: string;
  readonly version: number;
}
export interface CatalogueEntryDto {
  readonly adapter: string;
  readonly platform: string;
  readonly availability: 'development' | 'unavailable';
  readonly validatedVersions: readonly string[];
  readonly limitationKey: string;
  readonly runnable: boolean;
}
export interface MappingDto {
  readonly id: string;
  readonly locationId: string;
  readonly locationName: string;
  readonly externalRef: string;
  readonly status: 'active' | 'retired';
  readonly createdAt: string;
}
export interface ConnectorsResponse {
  readonly items: ConnectorDto[];
  readonly catalogue: CatalogueEntryDto[];
}

// ------------------------------------------------------------------ credentials (PB-11-02 .. PB-11-06, OR-15)
export const IssueCredentialRequest = z
  .object({
    participationId: Uuid,
    tokenType: z.enum(TOKEN_TYPE_VALUES),
    /** Requested zones (location ids). Omitted = every approved zone. Never broader than approved. */
    zones: z.array(Uuid).max(100).optional(),
  })
  .strict();
export const RevokeCredentialRequest = z.object({ reason: z.literal('manual').default('manual'), note: trimmed(500) });
export const RetryCredentialRequest = z.object({ expectedVersion: Version });
export const OperationVersionRequest = z.object({ expectedVersion: Version });
export const OperationDecisionRequest = z.object({
  expectedVersion: Version,
  decision: z.enum(['confirmed_revoked', 'not_issued', 'retry_revoke']),
  note: Note,
});

export interface ZonePreviewDto {
  readonly locationId: string;
  readonly name: string;
  readonly externalRef: string | null;
  readonly status: 'granted' | 'not_approved' | 'unmapped';
}
export interface SchedulePreviewDto {
  readonly kind: 'continuous' | 'daily';
  readonly validFrom: string;
  readonly validUntil: string;
  readonly dailyStart: string | null;
  readonly dailyEnd: string | null;
  readonly timeZone: string;
  readonly narrowed: boolean;
  readonly activationAt: string | null;
}
export interface IssuePreviewResponse {
  readonly participationId: string;
  readonly visitor: string;
  readonly reference: string;
  readonly eligible: boolean;
  readonly blockers: string[];
  readonly connector: { readonly id: string; readonly name: string; readonly adapter: string } | null;
  readonly allowedTokenTypes: string[];
  readonly zones: ZonePreviewDto[];
  readonly approvedZoneIds: string[];
  readonly schedule: SchedulePreviewDto | null;
  readonly scheduleProblem: string | null;
  readonly card: { readonly id: string; readonly number: string } | null;
}

export interface OperationDto {
  readonly id: string;
  readonly credentialId: string;
  readonly kind: 'issue' | 'revocation';
  readonly trigger: string;
  readonly state: string;
  readonly stateReason: string | null;
  readonly attempts: number;
  readonly readBacks: number;
  readonly createdAt: string;
  readonly availableAt: string;
  readonly lastAttemptAt: string | null;
  readonly settledAt: string | null;
  readonly owner: { readonly id: string; readonly name: string } | null;
  readonly resolution: string | null;
  readonly resolutionNote: string | null;
  readonly resolvedAt: string | null;
  readonly version: number;
}
export interface CredentialDto {
  readonly id: string;
  readonly siteId: string;
  readonly visitId: string;
  readonly reference: string;
  readonly participationId: string;
  readonly visitor: string;
  readonly presenceStatus: string;
  readonly tokenType: string;
  readonly card: { readonly id: string; readonly number: string; readonly custody: string } | null;
  readonly tokenHint: string | null;
  readonly state: string;
  readonly stateReason: string | null;
  readonly zones: Array<{ readonly locationId: string; readonly name: string; readonly externalRef: string }>;
  readonly schedule: SchedulePreviewDto;
  readonly confirmedScope: { readonly zones: string[]; readonly validFrom: string; readonly validUntil: string } | null;
  readonly externalRef: string | null;
  readonly revokeRequestedAt: string | null;
  readonly revokeReason: string | null;
  readonly connector: { readonly id: string; readonly name: string };
  readonly requestedAt: string;
  readonly requestedBy: string | null;
  readonly version: number;
  readonly openOperation: OperationDto | null;
}
export interface CredentialDetailResponse {
  readonly credential: CredentialDto;
  readonly operations: OperationDto[];
  readonly history: Array<{ readonly kind: string; readonly from: string | null; readonly to: string | null; readonly reason: string | null; readonly actor: string | null; readonly at: string; readonly operationId: string | null }>;
}
export interface CredentialListResponse {
  readonly items: CredentialDto[];
}
export interface CandidateDto {
  readonly participationId: string;
  readonly visitId: string;
  readonly reference: string;
  readonly visitor: string;
  readonly window: { readonly start: string; readonly end: string } | null;
  readonly presenceStatus: string;
  readonly credentialStatus: string;
}
export interface RecoveryItemDto {
  readonly operation: OperationDto;
  readonly credential: CredentialDto;
}
export interface RecoveryQueueResponse {
  readonly items: RecoveryItemDto[];
}

// ------------------------------------------------------------------ access events (PB-11-08)
export interface AccessEventDto {
  readonly id: string;
  readonly doorRef: string;
  readonly zone: string | null;
  readonly direction: 'entry' | 'exit' | 'unknown';
  readonly result: 'granted' | 'denied';
  readonly occurredAt: string;
  readonly receivedAt: string;
  readonly late: boolean;
  readonly confidence: 'exact' | 'outside_validity' | 'none';
  readonly sourceRef: string;
  readonly credentialId: string | null;
  readonly visitor: string | null;
  readonly reference: string | null;
}
export interface AccessEventsResponse {
  readonly items: AccessEventDto[];
}

// ------------------------------------------------------------------ badge templates (PB-11-01)
export const BadgeLayoutInput = z.object({
  size: z.enum(['cr80', 'a6']),
  fields: z.array(z.enum(BADGE_FIELD_VALUES)).min(1).max(10),
  accentColour: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'v.colour').nullable(),
  statusBand: z.boolean(),
});
export const CreateBadgeTemplateRequest = z.object({
  siteId: Uuid.nullable(),
  name: trimmed(120),
  categoryCode: z.string().trim().regex(/^[a-z][a-z0-9_-]{0,39}$/, 'v.policyKey').nullable(),
  accessStatus: z.enum(BADGE_ACCESS_STATUS_VALUES),
  layout: BadgeLayoutInput,
});
export const UpdateBadgeTemplateRequest = z.object({ expectedVersion: Version, name: trimmed(120).optional(), layout: BadgeLayoutInput.optional() });
export const BadgeTemplateVersionRequest = z.object({ expectedVersion: Version });

export interface BadgeLayoutDto {
  readonly size: 'cr80' | 'a6';
  readonly fields: string[];
  readonly accentColour: string | null;
  readonly statusBand: boolean;
}
export interface BadgeTemplateDto {
  readonly id: string;
  readonly siteId: string | null;
  readonly name: string;
  readonly categoryCode: string | null;
  readonly accessStatus: 'any' | 'with_credential' | 'without_credential';
  readonly number: number;
  readonly status: 'draft' | 'published' | 'retired' | 'discarded';
  readonly layout: BadgeLayoutDto;
  readonly publishedAt: string | null;
  readonly version: number;
}
export interface BadgeTemplatesResponse {
  readonly items: BadgeTemplateDto[];
}

// ------------------------------------------------------------------ badge layout rules (PB-11-01)
// Pure and browser-safe: shared by the API (validation at publication) and the web client (preview
// and printing).

export const BADGE_FIELDS = BADGE_FIELD_VALUES;
export type BadgeField = (typeof BADGE_FIELDS)[number];
/** Fields a badge can never drop: who, until when, and the desk reference. */
export const MANDATORY_BADGE_FIELDS: readonly BadgeField[] = ['visitorName', 'validUntil', 'reference'];

export const BADGE_SIZES = {
  // ISO/IEC 7810 ID-1 (CR80) card and A6 label, in millimetres.
  cr80: { widthMm: 85.6, heightMm: 54, nameChars: 22, maxNameLines: 2 },
  a6: { widthMm: 105, heightMm: 148, nameChars: 26, maxNameLines: 3 },
} as const;
export type BadgeSize = keyof typeof BADGE_SIZES;

export interface BadgeLayout {
  readonly size: BadgeSize;
  readonly fields: readonly BadgeField[];
  readonly accentColour: string | null;
  /** Print the access status as a band ("Temporary access" / "Escort required — not an access card"). */
  readonly statusBand: boolean;
}

export type BadgeProblem = { readonly path: string; readonly code: 'mandatory' | 'unknown_field' | 'duplicate' | 'colour' | 'size' };

export function validateBadgeLayout(l: BadgeLayout): BadgeProblem[] {
  const p: BadgeProblem[] = [];
  if (!(l.size in BADGE_SIZES)) p.push({ path: 'size', code: 'size' });
  const seen = new Set<string>();
  l.fields.forEach((f, i) => {
    if (!(BADGE_FIELDS as readonly string[]).includes(f)) p.push({ path: `fields.${i}`, code: 'unknown_field' });
    if (seen.has(f)) p.push({ path: `fields.${i}`, code: 'duplicate' });
    seen.add(f);
  });
  for (const m of MANDATORY_BADGE_FIELDS) if (!l.fields.includes(m)) p.push({ path: 'fields', code: 'mandatory' });
  if (l.accentColour !== null && !/^#[0-9a-fA-F]{6}$/.test(l.accentColour)) p.push({ path: 'accentColour', code: 'colour' });
  return p;
}

export interface BadgePlan {
  readonly nameLines: readonly string[];
  readonly nameTruncated: boolean;
  /** Regions that are always printed whatever the name length or photo (PB-11-01 acceptance). */
  readonly reserved: readonly ('validUntil' | 'reference' | 'referenceQr')[];
  readonly photo: 'shown' | 'placeholder' | 'none';
}

/**
 * Fit the visitor's name into the size's line budget, reserving the expiry and QR regions. A long
 * Arabic (or any) name is wrapped on word boundaries and truncated with an ellipsis instead of
 * pushing the expiry or the QR off the badge; a missing optional photo leaves a placeholder.
 */
export function planBadge(layout: BadgeLayout, name: string, hasPhoto: boolean): BadgePlan {
  const size = BADGE_SIZES[layout.size];
  const words = name.trim().split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';
  let truncated = false;
  for (const w of words) {
    const piece = w.length > size.nameChars ? `${w.slice(0, size.nameChars - 1)}…` : w;
    if (piece !== w) truncated = true;
    const next = current ? `${current} ${piece}` : piece;
    if (next.length <= size.nameChars) current = next;
    else {
      lines.push(current);
      current = piece;
    }
  }
  if (current) lines.push(current);
  let out = lines;
  if (lines.length > size.maxNameLines) {
    truncated = true;
    out = lines.slice(0, size.maxNameLines);
    const last = out[out.length - 1]!;
    out[out.length - 1] = `${last.slice(0, Math.max(1, size.nameChars - 1))}…`;
  }
  const reserved: BadgePlan['reserved'][number][] = ['validUntil', 'reference'];
  if (layout.fields.includes('referenceQr')) reserved.push('referenceQr');
  return { nameLines: out, nameTruncated: truncated, reserved, photo: layout.fields.includes('photo') ? (hasPhoto ? 'shown' : 'placeholder') : 'none' };
}

export const DEFAULT_BADGE_LAYOUT: BadgeLayout = {
  size: 'cr80',
  fields: ['visitorName', 'organisation', 'hostName', 'siteName', 'validUntil', 'reference', 'category'],
  accentColour: null,
  statusBand: true,
};
